import {
  CreditCardType,
  PetType,
  POWERUP_TYPE,
  PRODUCT_TYPE,
  PRODUCT_VALUES,
} from './constants';
import app from './app.json';

const CLIENT_NAME = 'EB';

// serializeForUri({ param0: 'value 0', param1: 'value:1' }) ===
// 'param0=value%200&param1=value%3A1'
export function serializeForUri(obj = {}) {
  return Object
    .keys(obj)
    .map((key) => `${encodeURI(key)}=${encodeURI(obj[key])}`)
    .join('&');
}

export function genApiUrl(baseURL, path, params) {
  let serializedParams = serializeForUri(params);

  if (serializedParams) {
    serializedParams = `?${serializedParams}`;
  }

  return `${baseURL}/${path}${serializedParams}`;
}

async function genHttpRequest(url, params = {}) {
  const options = {
    headers: {
      'Api-version': 'v1',
      Authorization: 'APIKEY 8f7ecbb6-a62c-44d9-ab88-7b295ba7956c',
      'Content-Type': 'application/json',
      'X-Api-Key': process.env.REACT_APP_XAPIKEY,
      ...(process.env.REACT_APP_SUBSCRIPTION_KEY
        ? {
          'Ocp-Apim-Subscription-Key':
            process.env.REACT_APP_SUBSCRIPTION_KEY,
        }
        : {}),
      'X-Client-Name': CLIENT_NAME,
      'X-Client-Version': app.version,
      ...params.headers,
    },
  };

  if (params.post) {
    options.method = 'POST';
    options.body = JSON.stringify(params.post);
  } else if (params.delete) {
    options.method = 'DELETE';
    options.body = JSON.stringify(params.delete);
  }

  let id = 0;

  if (params.withTimeout) {
    const timeoutTime = params.timeoutTime || 60000;
    if (AbortSignal.timeout) {
      options.signal = AbortSignal.timeout(timeoutTime);
    } else {
      // old browsers
      const controller = new AbortController();
      id = setTimeout(() => controller
        .abort('AbortError: timeout'), timeoutTime);
      options.signal = controller.signal;
    }
  }

  const response = await fetch(url, options);
  clearTimeout(id);

  return response;
}

export async function apiCall(path, params = {}) {
  const url = genApiUrl(process.env.REACT_APP_API_URL, path, params.get);

  return genHttpRequest(url, params);
}

export async function apiCallV2(path, params = {}) {
  const url = genApiUrl(process.env.REACT_APP_API_URL, path, params.get);

  return genHttpRequest(url, {
    ...params,
    headers:
    {
      ...params.headers,
      'Api-version': 'v2',
    },
  });
}

export function doNothing() {
  // Does exactly what it promises.
  // ... what is "nothing" anyway? https://bit.ly/2Hwo3Vj
}

export function setSessionStorage({ data, key }) {
  if (data && key) {
    sessionStorage.setItem(key, JSON.stringify(data));
  }
}

export function getSessionStorage(key) {
  const data = sessionStorage.getItem(key);

  if (data) {
    return JSON.parse(data);
  }

  return null;
}

export function removeSessionStorage(key) {
  sessionStorage.removeItem(key);
}

function camelize(text) {
  const camelRegex = /[-_\s.]+(.)?/g;
  const temp = text.replace(camelRegex, (_, c) => (
    c ? c.toUpperCase() : ''));
  return temp.substr(0, 1).toLowerCase() + temp.substr(1);
}

export function formatParameters(parametersRaw) {
  const parameters = {};
  Object.keys(parametersRaw).forEach(
    (key) => { parameters[camelize(key)] = parametersRaw[key]; },
  );
  return parameters;
}

export function formatDate(date) {
  if (date.toString().includes('T')) {
    return date.split('T')[0];
  }

  return date;
}

// This function uses setTimeout but can be combined with await in an async
// function to wait for some time before continuing execution.
export function sleep(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

/* eslint-disable no-bitwise */
export function generateUUID() {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = ((dt + Math.random()) * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
  return uuid;
}

export function getPetNameFromValue(petValue) {
  switch (petValue) {
    case PetType.Cat.value:
      return PetType.Cat.name;

    case PetType.Dog.value:
      return PetType.Dog.name;

    default:
      return 'undefined';
  }
}

export function validateEmail(email) {
  const regexString = '^[a-zA-Z0-9_-]+(\\.[a-zA-Z0-9_-]+)*@'
    + '[a-zA-Z0-9_-]+(\\.[a-zA-Z0-9_-]+)*\\.[a-zA-Z0-9]{1,}$';
  const regex = new RegExp(regexString);

  return regex.test(String(email).toLowerCase());
}

export function testOnlyNumber(value) {
  const REGEX_NUMBER = /^[0-9]*$/;

  return REGEX_NUMBER.test(value) || !value;
}

export function testContainLetter(value) {
  const REGEX_LETTER = /[a-zA-Z]/;

  return REGEX_LETTER.test(value);
}

export function testContainNumber(value) {
  const REGEX_ANY_NUMBER = /[0-9]/;

  return REGEX_ANY_NUMBER.test(value);
}

export function currencyFormat({ fixed = 2, value = 0 }) {
  if (value === 0) {
    return `${value.toFixed(fixed)}`;
  }

  const formattedValue = `${Math.abs(value)
    .toFixed(fixed).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;

  return formattedValue;
}

export const useElectronicConsent = app['fpi-10540'];
export const isPaycheck = app.paycheck;
export const {
  useProp103,
  useRetryPurchase,
} = app;

export function getProductType(productId) {
  const { FPI_VALUES, IHC_VALUES } = PRODUCT_VALUES;

  if (IHC_VALUES.indexOf(productId) >= 0) {
    return PRODUCT_TYPE.IHC;
  }

  if (FPI_VALUES.indexOf(productId) >= 0) {
    return PRODUCT_TYPE.FPI;
  }

  return PRODUCT_TYPE.Unknown;
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
let timeout;
export function debounce(func, wait, immediate) {
  clearTimeout(timeout);

  return function f(...args) {
    const context = this;

    function later() {
      timeout = null;

      if (!immediate) {
        func.apply(context, args);
      }
    }

    const callNow = immediate && !timeout;
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
}

export function phoneMask(value = '') {
  const sanitizeText = value ? value.replace(/-/g, '') : '';
  const formattedValue = sanitizeText.split(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    .filter((item) => item.length > 0);

  return formattedValue.join('-');
}

export function decodeImageBase64(imageEncoded) {
  return `data:image/png;base64,${imageEncoded}`;
}

export function isValidDiamondStreet(value) {
  const newValue = String(value).replace('P.O.', '').replace('P. O.', '');

  // TODO diamond issue FPA-6328
  // eslint-disable-next-line max-len
  const REGEX_STREET_ADDRESS = /^\s*((([a-zA-Z]+\s){1}((?=.*[0-9]+))|([0-9]+\s){1}((?=.*[a-zA-Z]+)))|((?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)+\s){1}((?=.*[0-9a-zA-Z]+)))+.*\s*$/;
  const isStreetAddressValid = REGEX_STREET_ADDRESS.test(newValue.trim());

  return isStreetAddressValid;
}

/**
 * store.data.insuranceProductEB.Id
 * @param {*} productId int
 */
export function isProductFPI(productId) {
  const { FPI_VALUES } = PRODUCT_VALUES;

  return FPI_VALUES.indexOf(productId) >= 0;
}

/**
 * store.data.insuranceProductEB.Id
 * @param {*} productId int
 */
export function isProductIPG(productId) {
  const { IPG_VALUES } = PRODUCT_VALUES;

  return IPG_VALUES.indexOf(productId) >= 0;
}

export function testNumberWithDecimal(value) {
  const REGEX_NUMBER = /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/;

  return REGEX_NUMBER.test(value) || !value;
}

/**
 * @param {string} stringToClean
 * @returns {string} The same stringToClean without emojis
 */
export function removeEmojis(stringToClean) {
  // eslint-disable-next-line max-len
  const REGEX_EMOJIS = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
  return stringToClean.replace(REGEX_EMOJIS, '');
}

export function scrollToElement(idElement) {
  try {
    const element = document.getElementById(idElement);
    window.scrollTo(element.offsetLeft, element.offsetTop);
  } catch (_) {
    doNothing();
  }
}

export function getElementOffsetTop(elementName = '', modifier = 0) {
  const element = document.getElementById(elementName);

  if (element) {
    return element.offsetTop - modifier;
  }

  return 0;
}

export function getCreditCardType(cardNumber) {
  const REGEX_AMEX = /^3[47]/;
  const REGEX_DINER_CLUB = /^3[068]/;
  const REGEX_VISA = /^4[0-9]/;

  if (REGEX_AMEX.test(cardNumber)) {
    return { length: 15, type: CreditCardType.AmericanExpress.name };
  }

  if (REGEX_VISA.test(cardNumber)) {
    return { length: 16, type: CreditCardType.Visa.name };
  }

  if (REGEX_DINER_CLUB.test(cardNumber)) {
    return { length: 14, type: CreditCardType.DinnersClub.name };
  }

  return {
    length: 16,
    type: CreditCardType.Other.name,
  };
}

export function formatDataDMY(utcFormatDate) {
  const splitedParts = utcFormatDate.split('-');
  return `${splitedParts[2]}-${splitedParts[1]}-${splitedParts[0]}`;
}

export function createEffectiveDate() {
  const date = new Date();
  date.setDate(date.getDate() + 1);

  return date.toISOString().split('T')[0];
}

export const removeDuplicates = (array, key) => {
  const uniqueSet = new Set();

  return array.filter((item) => {
    const value = key ? item[key] : item;
    if (!uniqueSet.has(value)) {
      uniqueSet.add(value);
      return true;
    }
    return false;
  });
};

export function validatePartnerIds({ parameters, synonyms }) {
  const response = {};
  const customParameter = Object.keys(parameters)
    .find((parameter) => synonyms
      .map((synonym) => synonym.toLowerCase())
      .includes(parameter.toLowerCase()));

  if (customParameter) {
    response.Name = customParameter;
    response.Value = parameters[customParameter];
  }

  return response;
}

export function loadEmbeddedScript({
  onErrorCallback = () => { },
  onLoadCallback,
  scriptAsync,
  scriptDefer,
  scriptId,
  scriptInnerHTML,
  scriptSrc,
  scriptType = 'text/javascript',
  toHead = false,
}) {
  if (document.getElementById(scriptId)) {
    return;
  }
  let script = document.createElement('script');
  if (scriptType === 'text/css') {
    script = document.createElement('style');
  }
  script.id = scriptId;
  script.type = scriptType;
  if (scriptSrc) {
    script.src = scriptSrc;
  }

  script.async = scriptAsync;
  script.defer = scriptDefer;

  if (scriptInnerHTML) {
    script.innerHTML = scriptInnerHTML;
  }
  if (onLoadCallback) {
    script.onload = () => onLoadCallback();
  }
  script.onerror = onErrorCallback;

  if (toHead) {
    document.head.appendChild(script);
    return;
  }
  document.body.appendChild(script);
}

/**
 * TODO: Simplify once the new prd is working fine
 * and Prod is completly removed
 */
export const isProdEnv = process.env.REACT_APP_ENVIRONMENT === 'prd'
  || process.env.REACT_APP_ENVIRONMENT === 'Prod'
  || process.env.REACT_APP_NAME === 'Prod'
  || process.env.REACT_APP_NAME === 'prd';

export function getUtmParams(parameters) {
  return Object.fromEntries(Object.entries(parameters)
    .filter(([key]) => key.startsWith('utm_')
      || key.startsWith('agentId')));
}

export function getAgeInYears(ageName = '') {
  if (ageName?.toLowerCase().includes('year')) {
    return ageName?.split(' ')[0];
  }

  return '0';
}

// Use this only if you can't use useRef,
// React not recommend manipulate DOM directly
export function forceFocusElement(id) {
  const element = document.getElementById(id);

  if (element) {
    element.focus();
  }
}

/**
 * @param {string} value String to force to boolean
 * @returns {boolean} the parsed value
 */
export function toBoolean(value = '') {
  return !!value.match(/^(true|1|yes)$/i);
}

export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getB2CRedirectUrl() {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:3000';
  }

  return process.env.REACT_APP_EB_URL;
}

/**
 * Validates if the whole employee field (from pet parent info section)
 * should be rendered or not.
 * @param {*} employeeIdHelpText This value comes from
 * backend side and it could be a string, null or empty.
 * @returns Bool rendering validation.
 */
export const shouldHideEmployeeField =
  (employeeIdHelpText = '') => employeeIdHelpText === null;

/**
 * Validates if the info bubble (from pet parent info section)
 * should be rendered or not.
 * @param {*} employeeIdHelpText This value comes from
 * backend side and it could be a string, null or empty.
 * @returns Bool rendering validation.
 */
export const shouldHideEmployeeInfoBubble =
  (employeeIdHelpText = '') => employeeIdHelpText === '';

export const appVersion = app.version;

/**
 * Validates if all the powerups have been reviewed by the user.
 * If not, returns an array with the powerup ids not reviewed
 * @param {object} petQuoteRateItem
 * @returns {{isValid: boolean, modifierIdNotSelectedList: int[]}}
 */
export const validatePowerupsSelected = (petQuoteRateItem) => {
  if (!petQuoteRateItem) {
    return { isValid: false, modifierIdNotSelectedList: [] };
  }
  const possiblePowerups = petQuoteRateItem.InsuranceModifiersEB.filter(
    (modifier) => modifier.InsuranceModifierTypeEBId === POWERUP_TYPE
      && modifier.IsVisible,
  );

  const modifierIdNotSelected = [];

  possiblePowerups.forEach((powerup) => {
    if (powerup.IsSelected === null || powerup.IsSelected === undefined) {
      modifierIdNotSelected.push(powerup.Id);
    }
  });

  return {
    isValid: !modifierIdNotSelected.length,
    modifierIdNotSelectedList: modifierIdNotSelected,
  };
};

export const messageError = (message) => {
  let messageLog = message;
  if (message.toLowerCase().includes('failed to fetch')
    || message.toLowerCase().includes('load failed')) {
    messageLog =
      'The request was not processed'
      + ' — no response was received from the endpoint';
    if (message.includes('FSC Leads')) {
      messageLog = `FSC Leads - ${messageLog}`;
    }
  }
  return messageLog;
};

export const maskRateData = (rateData) => {
  const newRate = {
    ...rateData,
    ebPetQuoteResponseList: rateData.ebPetQuoteResponseList.map((quote) => ({
      ...quote,
      InsuranceModifiersEB: quote.InsuranceModifiersEB.map((modifier) => ({
        ...modifier,
        IsSelected: !!modifier.IsSelected,
      })),
      modifiers: quote.modifiers.map((modifier) => ({
        ...modifier,
        isSelected: !!modifier.isSelected,
      })),
    })),
  };
  return newRate;
};

export const detectOS = () => {
  if (navigator.userAgentData) {
    return navigator.userAgentData.platform;
  }

  // Get from UserAgent
  const OS_RULES = [
    { name: 'Windows', regex: /Windows NT/ },
    { exclude: /iPhone|iPad|iPod/, name: 'macOS', regex: /Mac OS X/ },
    { name: 'Android', regex: /Android/ },
    { name: 'iOS', regex: /iPhone|iPad|iPod/ },
    { name: 'Linux', regex: /Linux/ },
    { name: 'Chrome OS', regex: /CrOS/ },
  ];
  const ua = navigator.userAgent;
  const rule = OS_RULES.find(
    ({ regex, exclude }) => regex.test(ua) && (!exclude || !exclude.test(ua)),
  );
  return rule?.name ?? 'Unknown';
};

export const getBrowserInfo = () => {
  let browserName = 'Unknown';
  let browserVersion;
  const ua = navigator.userAgent;

  if (navigator.userAgentData) {
    const brandLastIndex = navigator.userAgentData.brands.length - 1;
    const brand = navigator.userAgentData.brands[brandLastIndex];
    browserName = brand?.brand;
    browserVersion = brand?.version;
  } else {
    // Not Chromium
    const matchFirefox = ua.match(/Firefox\/(\d+)/);
    if (matchFirefox) {
      browserName = 'Firefox';
      // eslint-disable-next-line prefer-destructuring
      browserVersion = matchFirefox[1];
    } else if (/Safari\/(\d+)/.test(ua)) {
      browserName = 'Safari';
    }
  }

  return {
    name: browserName,
    userAgent: ua,
    version: browserVersion,
  };
};

export function mergeParameters(parameters = '', parametersToMerge = {}) {
  const finalParameters = new URLSearchParams(parameters);
  Object.entries(parametersToMerge).forEach(([key, value]) => {
    finalParameters.set(key, value);
  });
  return finalParameters.toString();
}
